import { Notification } from '@rio-cloud/rio-uikit';
import React, { useCallback, useRef, useState } from 'react';

interface VideoUploadProps {
    onVideoUploaded: (videoUrl: string) => void;
    onFileSelected: (file: File | null) => void;
    currentVideoUrl?: string;
    disabled?: boolean;
}

const VideoUpload: React.FC<VideoUploadProps> = ({
    onVideoUploaded,
    onFileSelected,
    currentVideoUrl,
    disabled = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): boolean => {
        const maxSize = 100 * 1024 * 1024; // 100MB
        const allowedTypes = [
            'video/mp4',
            'video/avi',
            'video/mov',
            'video/wmv',
            'video/flv',
            'video/webm',
            'video/quicktime',
            'video/x-msvideo',
            'video/x-ms-wmv',
            'video/x-flv',
            'video/x-matroska',
            'video/3gpp',
            'video/3gpp2'
        ];

        if (file.size > maxSize) {
            Notification.error('Arquivo muito grande. Tamanho máximo: 100MB');
            return false;
        }

        if (!allowedTypes.includes(file.type)) {
            Notification.error('Tipo de arquivo não suportado. Formatos aceitos: MP4, AVI, MOV, WMV, FLV, WEBM, MKV, 3GP');
            return false;
        }

        return true;
    };

    const handleFileSelect = useCallback((file: File) => {
        if (!validateFile(file)) return;

        setSelectedFile(file);
        setUploadError(null);
        onFileSelected(file);

        // Se há um arquivo selecionado, limpar o link de vídeo
        onVideoUploaded('');
    }, [onFileSelected, onVideoUploaded]);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);

        const file = event.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadError(null);
        onFileSelected(null);

        // Limpar o input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClearLink = () => {
        onVideoUploaded('');
    };

    const getUploadAreaClasses = () => {
        let classes = 'video-upload-area border-2 border-dashed rounded-lg padding-20 text-center cursor-pointer transition-all duration-200 width-100pct';

        if (disabled) {
            classes += ' border-color-gray bg-highlight-lightest cursor-not-allowed opacity-50';
        } else if (isDragOver) {
            classes += ' border-color-primary bg-highlight-lightest';
        } else if (uploadError) {
            classes += ' border-color-danger bg-highlight-lightest';
        } else if (currentVideoUrl || selectedFile) {
            classes += ' border-color-success bg-highlight-lightest';
        } else {
            classes += ' border-color-gray bg-highlight-lightest hover-border-color-primary';
        }

        return classes;
    };

    return (
        <div className="video-upload-container">
            <label className="form-label text-size-14 text-normal text-color-dark line-height-16 mb-2 d-block">
                Vídeo *
            </label>

            <div
                className={getUploadAreaClasses()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={disabled}
                />

                {currentVideoUrl && !selectedFile ? (
                    <div className="video-preview text-align-left">
                        <div className="rioglyph rioglyph-check-circle text-size-32 text-color-success margin-bottom-10"></div>
                        <p className="text-size-14 text-color-dark margin-bottom-10">Link de vídeo configurado</p>
                        <div className="video-url bg-highlight-lightest padding-10 rounded text-size-12 text-color-dark break-all">
                            {currentVideoUrl}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearLink();
                            }}
                            className="btn btn-outline-warning text-size-12 padding-8 padding-x-16 rounded margin-top-10"
                        >
                            Limpar link
                        </button>
                    </div>
                ) : selectedFile ? (
                    <div className="video-preview text-align-left">
                        <div className="rioglyph rioglyph-check-circle text-size-32 text-color-success margin-bottom-10"></div>
                        <p className="text-size-14 text-color-dark margin-bottom-10">Arquivo selecionado</p>
                        <p className="text-size-12 text-color-gray margin-bottom-10">O arquivo será enviado ao salvar</p>
                        <div className="video-url bg-highlight-lightest padding-10 rounded text-size-12 text-color-dark break-all">
                            {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile();
                            }}
                            className="btn btn-outline-danger text-size-12 padding-8 padding-x-16 rounded margin-top-10"
                        >
                            Remover arquivo
                        </button>
                    </div>
                ) : uploadError ? (
                    <div className="upload-error text-align-left">
                        <div className="rioglyph rioglyph-error text-size-32 text-color-danger margin-bottom-10"></div>
                        <p className="text-size-14 text-color-dark margin-bottom-10">Falha na seleção</p>
                        <p className="text-size-12 text-color-gray margin-bottom-10">{uploadError}</p>
                        <p className="text-size-12 text-color-primary margin-bottom-10">Escolha uma opção para continuar ou arraste o arquivo aqui</p>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            className="btn btn-primary text-size-12 padding-8 padding-x-16 rounded"
                        >
                            Selecionar arquivo
                        </button>
                    </div>
                ) : (
                    <div className="upload-prompt text-align-left">
                        <div className="rioglyph rioglyph-cloud-upload text-size-32 text-color-gray margin-bottom-10"></div>
                        <p className="text-size-14 text-color-dark margin-bottom-10">
                            Arraste e solte um vídeo aqui ou clique para selecionar
                        </p>
                        <p className="text-size-12 text-color-gray">
                            Formatos: MP4, AVI, MOV, WMV, FLV, WEBM, MKV, 3GP (máx. 100MB)
                        </p>
                    </div>
                )}
            </div>

            {selectedFile && (
                <div className="margin-top-10">
                    <p className="text-size-12 text-color-info">
                        <span className="rioglyph rioglyph-info-circle margin-right-5"></span>
                        O arquivo será enviado ao salvar o formulário
                    </p>
                </div>
            )}

            {!selectedFile && !currentVideoUrl && (
                <div className="margin-top-10">
                    <p className="text-size-12 text-color-warning">
                        <span className="rioglyph rioglyph-info-circle margin-right-5"></span>
                        Selecione um arquivo de vídeo ou preencha um link de vídeo
                    </p>
                </div>
            )}
        </div>
    );
};

export default VideoUpload; 